import type {
  DealOperation,
  DealPropertyStatus,
  DealStatus,
  ParticipantRole,
  PropertyStatus,
  PropertyType,
  TimelineKind,
} from '@erp/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from './api.ts';

// Types mirror the API's responses (apps/api/src/deals/deals.types.ts).

export interface Stage {
  id: string;
  name: string;
  position: number;
  probability: number;
  staleDays: number | null;
  archived: boolean;
}

export interface Pipeline {
  id: string;
  name: string;
  operation: DealOperation;
  isDefault: boolean;
  archived: boolean;
  stages: Stage[];
}

export interface LostReason {
  id: string;
  name: string;
  active: boolean;
}

export interface DealSummary {
  id: string;
  title: string;
  operation: DealOperation;
  status: DealStatus;
  pipeline: { id: string; name: string };
  stage: { id: string; name: string; probability: number };
  contact: { id: string; name: string };
  estimatedAmount: number | null;
  currency: string;
  assignedTo: { membershipId: string; name: string };
  channel: { id: string; name: string } | null;
  stale: boolean;
  nextActivity: {
    id: string;
    title: string;
    startsAt: string;
    allDay: boolean;
    overdue: boolean;
  } | null;
  stageEnteredAt: string;
  lastActivityAt: string;
  closedAt: string | null;
  createdAt: string;
}

export interface DealDetail extends DealSummary {
  interest: {
    propertyTypes: PropertyType[];
    districts: { id: number; name: string; municipality: string }[];
    budgetMin: number | null;
    budgetMax: number | null;
    bedroomsMin: number | null;
    bathroomsMin: number | null;
    parkingMin: number | null;
  };
  lostReason: { id: string; name: string } | null;
  lostComment: string | null;
  utm: { source: string | null; medium: string | null; campaign: string | null };
  properties: {
    linkId: string;
    status: DealPropertyStatus;
    property: {
      id: string;
      code: string;
      title: string;
      status: PropertyStatus;
      salePrice: number | null;
      rentPrice: number | null;
      district: string;
      coverUrl: string | null;
      visible: boolean;
    };
  }[];
  participants: { id: string; role: ParticipantRole; contact: { id: string; name: string } }[];
  createdBy: string | null;
  updatedAt: string;
  can: { edit: boolean; delete: boolean };
}

export type TimelineItem =
  | {
      type: 'note';
      id: string;
      at: string;
      by: string | null;
      body: string;
      mentions: { membershipId: string; name: string }[];
    }
  | {
      type: 'event';
      id: string;
      at: string;
      by: string | null;
      kind: string;
      data: Record<string, unknown>;
    };

export interface Suggestion {
  id: string;
  code: string;
  title: string;
  type: PropertyType;
  salePrice: number | null;
  rentPrice: number | null;
  district: string;
  bedrooms: number | null;
  coverUrl: string | null;
  match: number;
  missing: string[];
}

export interface Board {
  pipeline: { id: string; name: string };
  stages: {
    id: string;
    name: string;
    probability: number;
    staleDays: number | null;
    total: number;
    amount: number;
    deals: DealSummary[];
  }[];
}

export interface DealFilters {
  q?: string;
  pipelineId?: string;
  stageId?: string;
  status?: DealStatus;
  channelId?: string;
  assignedMembershipId?: string;
  createdFrom?: string;
  createdTo?: string;
  stale?: 'true';
  noNextActivity?: 'true';
  page?: number;
}

export const dealKeys = {
  all: ['deals'] as const,
  list: (filters: DealFilters) => ['deals', 'list', filters] as const,
  board: (pipelineId: string, assigned: string | undefined) =>
    ['deals', 'board', pipelineId, assigned ?? null] as const,
  detail: (id: string) => ['deals', 'detail', id] as const,
  timeline: (id: string, kind: TimelineKind | undefined) =>
    ['deals', 'timeline', id, kind ?? null] as const,
  mentionable: (id: string) => ['deals', 'mentionable', id] as const,
  suggestions: (id: string) => ['deals', 'suggestions', id] as const,
  forContact: (contactId: string) => ['deals', 'contact', contactId] as const,
  trash: ['deals', 'trash'] as const,
  pipelines: ['pipelines'] as const,
  lostReasons: ['lost-reasons'] as const,
};

function toQuery(filters: DealFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

export const usePipelines = (archived = false) =>
  useQuery({
    queryKey: [...dealKeys.pipelines, archived],
    queryFn: () => api<Pipeline[]>(`/pipelines${archived ? '?archived=true' : ''}`),
    staleTime: 5 * 60_000,
  });

export const useLostReasons = (all = false) =>
  useQuery({
    queryKey: [...dealKeys.lostReasons, all],
    queryFn: () => api<LostReason[]>(`/lost-reasons${all ? '?all=true' : ''}`),
    staleTime: 5 * 60_000,
  });

export const useDeals = (filters: DealFilters, enabled = true) =>
  useQuery({
    queryKey: dealKeys.list(filters),
    queryFn: () => api<{ total: number; items: DealSummary[] }>(`/deals${toQuery(filters)}`),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useBoard = (pipelineId: string | undefined, assigned: string | undefined) =>
  useQuery({
    queryKey: dealKeys.board(pipelineId ?? '', assigned),
    queryFn: () =>
      api<Board>(
        `/deals/board?pipelineId=${pipelineId ?? ''}${assigned ? `&assignedMembershipId=${assigned}` : ''}`,
      ),
    enabled: Boolean(pipelineId),
    placeholderData: keepPreviousData,
  });

export const useDeal = (id: string) =>
  useQuery({
    queryKey: dealKeys.detail(id),
    queryFn: () => api<DealDetail>(`/deals/${id}`),
    retry: false,
  });

export const useTimeline = (id: string, kind: TimelineKind | undefined) =>
  useQuery({
    queryKey: dealKeys.timeline(id, kind),
    queryFn: () => api<TimelineItem[]>(`/deals/${id}/timeline${kind ? `?kind=${kind}` : ''}`),
  });

export const useMentionable = (id: string) =>
  useQuery({
    queryKey: dealKeys.mentionable(id),
    queryFn: () =>
      api<{ membershipId: string; name: string; canSee: boolean }[]>(`/deals/${id}/mentionable`),
    staleTime: 60_000,
  });

export const useSuggestions = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: dealKeys.suggestions(id),
    queryFn: () => api<Suggestion[]>(`/deals/${id}/suggestions`),
    enabled,
  });

export const useContactDeals = (contactId: string) =>
  useQuery({
    queryKey: dealKeys.forContact(contactId),
    queryFn: () =>
      api<{ items: DealSummary[]; hiddenCount: number }>(`/contacts/${contactId}/deals`),
  });
